'use client';

import type { LangbridgeConfig } from './api';

/** localStorage keys (shared with the reference console's convention). */
const API_KEY = 'lb_api';
const PW_KEY = 'lb_chat_pw';
const AGENT_KEY = 'lb_agent_id';

/**
 * Connection injected by a host shell — the Electron desktop app's preload bridge
 * sets `window.langbridge` to its bundled local backend's origin, random chat
 * password, and the single bound agent id. This takes priority over localStorage
 * so the packaged app auto-connects with no connect form.
 */
export interface InjectedLangbridgeConfig {
  apiBase?: string;
  password?: string;
  agentId?: string;
  /** Central storage server for uploads (desktop only; see api.ts). */
  uploadBase?: string;
  /** Chat password for the central storage server (desktop only). */
  uploadPassword?: string;
}

declare global {
  interface Window {
    langbridge?: InjectedLangbridgeConfig;
  }
}

function injected(): InjectedLangbridgeConfig {
  if (typeof window === 'undefined') return {};
  return window.langbridge ?? {};
}

/**
 * Default connection: the live LangBridge deployment (same contract as the local
 * `yarn start:local` backend on :3001). Override apiBase to point at localhost for
 * local dev. Password default is the shared chat-console password.
 */
export const DEFAULT_CONFIG: LangbridgeConfig = {
  apiBase: 'https://langbridge.laivdata.com',
  password: 'laivdata',
};

export function loadConfig(): LangbridgeConfig {
  if (typeof window === 'undefined') return { ...DEFAULT_CONFIG };
  // Injected shell config wins, then persisted localStorage, then the default.
  const inj = injected();
  // Only present when a host shell (desktop) injects them; undefined on web so
  // the upload util falls back to apiBase/password (same-server presign).
  const uploadFields = {
    ...(inj.uploadBase ? { uploadBase: inj.uploadBase } : {}),
    ...(inj.uploadPassword ? { uploadPassword: inj.uploadPassword } : {}),
  };
  try {
    return {
      apiBase: inj.apiBase ?? localStorage.getItem(API_KEY) ?? DEFAULT_CONFIG.apiBase,
      password: inj.password ?? localStorage.getItem(PW_KEY) ?? DEFAULT_CONFIG.password,
      ...uploadFields,
    };
  } catch {
    return {
      apiBase: inj.apiBase ?? DEFAULT_CONFIG.apiBase,
      password: inj.password ?? DEFAULT_CONFIG.password,
      ...uploadFields,
    };
  }
}

export function saveConfig(cfg: LangbridgeConfig): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(API_KEY, cfg.apiBase);
    localStorage.setItem(PW_KEY, cfg.password ?? '');
  } catch {
    /* noop */
  }
}

export function loadAgentId(): string | null {
  if (typeof window === 'undefined') return null;
  // The shell binds a single fixed agent; it wins over any persisted selection.
  const inj = injected();
  if (inj.agentId) return inj.agentId;
  try {
    return localStorage.getItem(AGENT_KEY);
  } catch {
    return null;
  }
}

export function saveAgentId(agentId: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(AGENT_KEY, agentId);
  } catch {
    /* noop */
  }
}
